import { useDisplayTz } from "@/hooks/use-display-tz";
import {
  SkyButton,
  SkyCard,
  SkyCheckbox,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
  SkyRadioGroup,
  SkyText,
  SkyTimePicker,
  type CheckboxState,
} from "@skypointcloud/skypoint-ui";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type Ref,
  type RefObject,
} from "react";
import {
  MAX_RUN_DATES,
  MONTH_NAMES,
  describeCron,
  type IntervalMinutes,
} from "@shared/cron/cronUtils";
import { isSelectableTimezone } from "@shared/types/schedules";
import { useMinuteClock } from "@/hooks/use-minute-clock";
import { AddIcon, DismissIcon } from "@/lib/icons";
import {
  isLegacyUsTimezone,
  offeredTimezones,
  timezoneName,
  timezonePickerLabel,
  timezoneShortLabel,
} from "@/lib/timezones";
import { ScheduleSkipDates } from "./ScheduleSkipDates";
import {
  INCOMPLETE_RUN_DATE_ISSUE,
  INCOMPLETE_TIME_ISSUE,
  INTERVAL_OPTIONS,
  MONTH_DAYS,
  NO_UPCOMING_RUN_EDITOR,
  ONCE_AVAILABLE,
  ONCE_COMING_SOON,
  QUARTER_HOUR_STEP,
  SCHEDULE_SEGMENTS,
  SKIP_RULE_SENTENCE,
  WEEK_DAYS,
  WINDOW_END_HOURS,
  WINDOW_START_HOURS,
  announceSchedule,
  applySegment,
  fromPickerTime,
  hasChosenTime,
  isPaused,
  offQuarterMinuteOf,
  offQuarterNote,
  resolveSchedule,
  runDateRowIssues,
  scheduleSummaryLine,
  shownSegment,
  splitTime,
  timeRowIssues,
  toPickerTime,
  todayInZone,
  windowHourLabel,
  withYearMonth,
  yearDayOptions,
  type ResolvedSchedule,
  type ScheduleDraft,
  type ScheduleSegment,
  formatPauseEnd,
} from "./scheduleForm";

/**
 * When: frequency, times or window, days and timezone (ported from Studio's `ScheduleTimeV2`).
 *
 * The donor's shape is kept (weekday checkboxes for Weekly, a day-of-month multiselect for
 * Monthly), and several things are different.
 *
 * **Frequency is a segmented control** (round 10, C1, in the style of Claude Routines): Once,
 * Hourly, Daily, Weekdays, Weekly, Monthly, Yearly and Custom, one radio group (arrow keys move
 * between them). Each maps onto the builder (`applySegment`); a live line under it says what the
 * draft runs and when next (`scheduleSummaryLine`). Once is shown disabled, "Coming soon", while
 * `ONCE_AVAILABLE` is false.
 *
 * **Each time is a `SkyTimePicker`** (12-hour) since 2026-09-28 (owner: "three dropdowns for
 * Hour Minute AM/PM seems very outdated"). skypoint-ui 2.17.0 passes axe closed and open
 * (`skypointUi.libraryContract.test.tsx`); 2.4.0 did not, which is why this card had three
 * dropdowns. Since 2.18.0 it has a Done button and no Now or Clear (`TimeRow`). The minute
 * step is 15 since round 5 (owner, 2026-10-01: "yes, go with 15-minute steps"): the column
 * offers :00, :15, :30 and :45, the quarter hours the server accepts for a new or changed
 * time. A schedule stored at another minute (02:07) still reads 02:07 in the text box
 * (the library formats the value, not the column), says so under the rows, and saves unchanged; a
 * pick in its picker moves it to a quarter hour (the column starts at :00 for :07). The draft
 * still holds 24-hour `HH:MM`, so nothing downstream changed.
 *
 * **A row with no time is a row the user must deal with** (2026-09-28, owner: "selected only the
 * hour ... it just saved and ignored"). "Add another time" puts an empty row INTO the draft;
 * until it has a time, or is removed, Save is blocked and the row says "Choose a time, or remove
 * this row." once the user has tried Save or opened the row's picker and closed it empty; a row
 * that has only just appeared is not an error yet (review r3 nit). The same time twice is named on the second row ("2:30 AM is already in this
 * schedule."). The picker has no half-chosen state: its first click fills the whole time (the
 * columns start at the current time, a library behaviour), and the row shows it at once.
 *
 * **Nothing is pre-selected but the zone** (Session 10, B4, SF1). A new schedule starts with no
 * frequency and an empty picker; the zone is pre-filled with the browser's because it is a zone,
 * not a time, and Save states it.
 *
 * **Every 15, 30 or 60 minutes** (2026-09-28): a step, a window (From is a run; Until is not, as
 * one cron expression cannot fire at 6:00 PM without firing at 6:30 PM) and weekdays, in place of
 * the time rows.
 *
 * The resolved schedule is stated in words in a polite live region, so a keyboard or
 * screen-reader user hears the RESULT of each change rather than having to parse
 * `0 2 * * 1,3,5`, and under it the platform's rule for a firing whose previous run is still
 * going. The cron equivalent is shown back, read-only, never typed.
 */

/**
 * Session 10, P9 (review r1 nit): the "No upcoming run" and "Paused until" bars state the saved
 * row, rendered with the page. `SkyMessageBar` hard-codes `role` and `aria-live` by intent
 * (warning = `alert`, assertive), so a screen reader would interrupt the page load to announce
 * them; they are read in document order instead. As `NoticeRegion`, the attributes are removed
 * through the forwarded ref before paint; React writes them again only if `intent` changes,
 * which it never does for these bars.
 */
function quietBar(bar: HTMLDivElement | null): void {
  bar?.removeAttribute("role");
  bar?.removeAttribute("aria-live");
}

/**
 * One row of the run times: a time the draft holds, or `null` for a row with no time yet.
 *
 * Keyed by a row id of its own (`useTimeRowIds`), so a row that gets its time keeps the same
 * picker (and the focus in it), and a row removed from the middle takes its state with it.
 * The group keeps the row's name ("Run at", or "Run 1 at", "Run 2 at" when there are several:
 * round 10, C1) for anything that finds a row by it.
 *
 * The picker is the schedule shape of `SkyTimePicker` (skypoint-ui 2.18.0, skypoint-ui#395, filed
 * from this card): a Done button closes it, as a time can take three picks (hour, minute, AM/PM)
 * and a column click never closes it; no "Now", as the current clock time means nothing for a
 * recurring time; no "Clear", as a time leaves with its row's Remove button and a schedule's only
 * time cannot be emptied. The library returns focus on close (to the field or the clock button
 * that opened it), and `onOpenChange` tells the row its picker was opened and shut; the app's
 * 2.17.0 workaround (a `MutationObserver` on `aria-expanded` and focus timers) is gone.
 */
function TimeRow({
  index,
  value,
  label,
  issue,
  revealIncomplete,
  disabled,
  onChange,
}: {
  index: number;
  value: string | null;
  label: string;
  issue: string | null;
  /** Show "Choose a time, or remove this row." now: the user has tried Save. */
  revealIncomplete: boolean;
  disabled: boolean;
  onChange: (value: string | null) => void;
}) {
  // The picker was opened and closed: from then on an empty row says so.
  const [closedOnce, setClosedOnce] = useState(false);
  const shown = issue === INCOMPLETE_TIME_ISSUE && !revealIncomplete && !closedOnce ? null : issue;
  return (
    <fieldset
      aria-label={label}
      className="min-w-0 flex-1 sm:max-w-xs"
      data-testid={`schedule-time-${index}`}
    >
      <SkyTimePicker
        label={label}
        placeholder="Choose a time"
        timeFormat="12h"
        minuteStep={QUARTER_HOUR_STEP}
        showDone
        showNow={false}
        showClear={false}
        onOpenChange={(open) => {
          if (!open) setClosedOnce(true);
        }}
        value={toPickerTime(value)}
        onSelectTime={(time) => onChange(fromPickerTime(time))}
        disabled={disabled}
        validationState={shown ? "error" : "none"}
        validationMessage={shown ?? undefined}
        testId={`schedule-time-${index}-picker`}
      />
    </fieldset>
  );
}

export interface ScheduleTimeSectionProps {
  draft: ScheduleDraft;
  onChange: (draft: ScheduleDraft) => void;
  /**
   * The zone the SAVED schedule holds, when there is one.
   *
   * The product offers seven US zones, and a row written by the dev seed, the walkthrough or an
   * earlier build can hold something else. That value is the row's, not the draft's: taking it
   * from the draft would lose it the moment the user tried another zone, and with it both the
   * note and the only option that could put the schedule back.
   */
  storedTimezone?: string | null;
  /** Field issues the server answered with, keyed by the field it named. */
  fieldErrors?: Record<string, string>;
  disabled?: boolean;
  /**
   * The SAVED schedule is enabled and has no next run (Session 10, P9): its skip dates leave no
   * firing inside two years, or its cron has none. Only a save recomputes it, so the card says so.
   */
  storedNoUpcomingRun?: boolean;
  /**
   * The user tried Save while it was blocked: a time row with no time says so from now on. Before
   * that, a row just added with "Add another time" is not marked as an error (Save is still
   * blocked, and the save bar says why).
   */
  revealIncompleteRows?: boolean;
  /** The card, so a failed save can scroll to it and focus it. */
  ref?: Ref<HTMLElement>;
}

/** What the server said about the expression itself (`cron`, or any `friendly.*` path). */
function scheduleIssueOf(fieldErrors: Record<string, string>): string {
  return Object.entries(fieldErrors)
    .filter(([field]) => field === "cron" || field === "friendly" || field.startsWith("friendly."))
    .map(([, issue]) => issue)
    .join(" ");
}

/** The stored expression the builder cannot represent, kept until a frequency and time are chosen. */
function keptCustomCron(draft: ScheduleDraft): string | null {
  return draft.customCron && !hasChosenTime(draft) ? draft.customCron : null;
}

function timeLabelOf(time: string): string {
  const parts = splitTime(time);
  return `${parts.hour}:${parts.minute} ${parts.period}`;
}

/** "Run at" alone; "Run 1 at", "Run 2 at" when there are several (round 10, C1). */
function timeRowLabel(index: number, count: number): string {
  return count === 1 ? "Run at" : `Run ${index + 1} at`;
}

/** The row ids in step with the rows, and the id the next added row takes. */
interface TimeRowIds {
  ids: number[];
  next: number;
}

/** Fit the ids to `count` rows: keep the first ones, give each new row the next id. */
function fitTimeRowIds(state: TimeRowIds, count: number): TimeRowIds {
  if (state.ids.length >= count) return { ids: state.ids.slice(0, count), next: state.next };
  const added = Array.from({ length: count - state.ids.length }, (_, i) => state.next + i);
  return { ids: [...state.ids, ...added], next: state.next + added.length };
}

/**
 * A stable id for each time row, the row's React key. The index is not one: a row can be removed
 * from the middle, and keyed by index its own state (whether its picker was opened and shut) and
 * the picker's would pass to the row that slides into its place. The time is not one either: two
 * rows may hold the same time while the user is fixing one of them.
 *
 * "Add another time" and Remove change the ids with the rows; when the rows change any other way
 * (a saved schedule is loaded), the ids are fitted to the new count, keeping the first ones.
 */
function useTimeRowIds(count: number): {
  ids: number[];
  add: () => void;
  remove: (index: number) => void;
} {
  const [state, setState] = useState<TimeRowIds>(() => fitTimeRowIds({ ids: [], next: 0 }, count));
  let current = state;
  if (state.ids.length !== count) {
    current = fitTimeRowIds(state, count);
    setState(current);
  }
  return {
    ids: current.ids,
    add: () => setState((s) => ({ ids: [...s.ids, s.next], next: s.next + 1 })),
    remove: (index) =>
      setState((s) => ({ ids: s.ids.filter((_, i) => i !== index), next: s.next })),
  };
}

/**
 * The zone notes under the zone picker: every time on this card is in the schedule's zone
 * (round 6, W8), and a stored zone that is not on the list keeps running there.
 */
function ZoneNotes({
  timezone,
  storedOutsideList,
}: {
  timezone: string;
  storedOutsideList: string | null;
}) {
  return (
    <>
      {timezone ? (
        <SkyText size={200} color="muted" block className="mt-1" testId="schedule-times-zone">
          {`Times are in ${timezoneShortLabel(timezone)}.`}
        </SkyText>
      ) : null}
      {storedOutsideList ? (
        <SkyText size={200} color="muted" block className="mt-1">
          {isLegacyUsTimezone(storedOutsideList)
            ? `This schedule is stored in ${timezoneName(storedOutsideList)}, which is no longer on the list. It keeps running in that zone until you choose another and save.`
            : `This schedule is stored in ${timezoneName(storedOutsideList)}, which is not on the list. It keeps running in that zone until you choose another and save.`}
        </SkyText>
      ) : null}
    </>
  );
}

/** Interval: the step and the window it repeats in. */
function IntervalFieldset({
  draft,
  onChange,
  disabled,
}: {
  draft: ScheduleDraft;
  onChange: (draft: ScheduleDraft) => void;
  disabled: boolean;
}): ReactElement {
  return (
    <fieldset className="border-0 p-0" data-testid="schedule-interval">
      <legend className="mb-2">
        <SkyText size={300} weight="semibold">
          Repeats
        </SkyText>
      </legend>
      <div className="grid gap-4 sm:grid-cols-3">
        <SkyDropdown
          label="Run every"
          required
          placeholder="Choose"
          options={INTERVAL_OPTIONS.map((option) => ({
            value: String(option.value),
            text: option.text,
          }))}
          selectedOptions={draft.intervalMinutes ? [String(draft.intervalMinutes)] : []}
          onOptionSelect={(values) =>
            onChange({
              ...draft,
              intervalMinutes: values[0]
                ? (Number(values[0]) as IntervalMinutes)
                : draft.intervalMinutes,
            })
          }
          disabled={disabled}
          testId="schedule-interval-minutes"
        />
        <SkyDropdown
          label="Start time"
          required
          placeholder="Choose"
          options={WINDOW_START_HOURS.map((hour) => ({
            value: String(hour),
            text: windowHourLabel(hour),
          }))}
          selectedOptions={draft.windowStart === null ? [] : [String(draft.windowStart)]}
          onOptionSelect={(values) =>
            onChange({
              ...draft,
              windowStart: values[0] ? Number(values[0]) : draft.windowStart,
            })
          }
          listboxMaxHeight="16rem"
          disabled={disabled}
          testId="schedule-window-start"
        />
        <SkyDropdown
          label="End time"
          required
          placeholder="Choose"
          options={WINDOW_END_HOURS.map((hour) => ({
            value: String(hour),
            text: windowHourLabel(hour),
          }))}
          selectedOptions={draft.windowEnd === null ? [] : [String(draft.windowEnd)]}
          onOptionSelect={(values) =>
            onChange({
              ...draft,
              windowEnd: values[0] ? Number(values[0]) : draft.windowEnd,
            })
          }
          listboxMaxHeight="16rem"
          disabled={disabled}
          testId="schedule-window-end"
        />
      </div>
      <SkyText size={200} color="muted" block className="mt-1" testId="schedule-window-hint">
        The window includes the start time and ends before the end time: every 30 minutes from 10:00
        AM until 6:00 PM runs at 10:00 AM, 10:30 AM and so on, last at 5:30 PM.
      </SkyText>
    </fieldset>
  );
}

/** The times of day, one row each, with Add another time and the quarter-hour rule. */
function TimesFieldset({
  timesRef,
  rows,
  rowIds,
  rowIssues,
  revealIncompleteRows,
  disabled,
  canAdd,
  offQuarterMinute,
  onTimeChange,
  onRemove,
  onAdd,
}: {
  timesRef: RefObject<HTMLFieldSetElement | null>;
  rows: Array<string | null>;
  rowIds: number[];
  rowIssues: Array<string | null>;
  revealIncompleteRows: boolean;
  disabled: boolean;
  canAdd: boolean;
  offQuarterMinute: number | null;
  onTimeChange: (index: number, value: string | null) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
}): ReactElement {
  return (
    <fieldset ref={timesRef} className="border-0 p-0">
      {/* One row is named by its own label, "Run at"; several are a list (round 10, C1). */}
      {rows.length > 1 ? (
        <legend className="mb-2">
          <SkyText size={300} weight="semibold">
            Run times
          </SkyText>
        </legend>
      ) : null}
      <div className="flex flex-col gap-3">
        {rows.map((time, index) => (
          // Keyed by the row's own id, not its index or its time (`useTimeRowIds`). A row that
          // gets its time keeps its picker, and the focus in it.
          <div key={`time-${rowIds[index]}`} className="flex items-start gap-2">
            <TimeRow
              index={index}
              value={time}
              label={timeRowLabel(index, rows.length)}
              issue={rowIssues[index] ?? null}
              revealIncomplete={revealIncompleteRows}
              disabled={disabled}
              onChange={(value) => onTimeChange(index, value)}
            />
            {rows.length > 1 ? (
              <SkyButton
                appearance="subtle"
                icon={DismissIcon}
                // Level with the text box: the row's label sits above it.
                className="mt-6"
                aria-label={
                  time === null ? "Remove the new time" : `Remove the ${timeLabelOf(time)} time`
                }
                disabled={disabled}
                onClick={() => onRemove(index)}
              />
            ) : null}
          </div>
        ))}
      </div>
      <SkyButton
        appearance="subtle"
        size="small"
        icon={AddIcon}
        className="mt-2"
        disabled={disabled || !canAdd}
        onClick={onAdd}
        testId="schedule-add-time"
      >
        Add another run time
      </SkyButton>
      {offQuarterMinute !== null ? (
        <SkyText size={200} block className="mt-2" testId="schedule-off-quarter-note">
          {offQuarterNote(offQuarterMinute)}
        </SkyText>
      ) : null}
      <SkyText size={200} color="muted" block className="mt-1" testId="schedule-same-minute-hint">
        Times run on the quarter hour (:00, :15, :30 or :45), and times in one schedule share the
        same minute. Create a second schedule for a time at a different minute.
      </SkyText>
    </fieldset>
  );
}

/**
 * Yearly (round 10, C1): a Month and a Day. The days offered are the ones the month has every
 * year (February to 28), so April 31 and February 29 cannot be chosen; the server refuses both.
 */
function YearDateFields({
  draft,
  onChange,
  disabled,
}: {
  draft: ScheduleDraft;
  onChange: (draft: ScheduleDraft) => void;
  disabled: boolean;
}): ReactElement {
  const { month, day } = draft.yearDate;
  return (
    <div className="grid gap-4 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
      <SkyDropdown
        label="Month"
        required
        placeholder="Choose a month"
        options={MONTH_NAMES.map((name, index) => ({ value: String(index + 1), text: name }))}
        selectedOptions={month === null ? [] : [String(month)]}
        onOptionSelect={(values) => {
          if (values[0]) onChange(withYearMonth(draft, Number(values[0])));
        }}
        listboxMaxHeight="16rem"
        disabled={disabled}
        testId="schedule-year-month"
      />
      <SkyDropdown
        label="Day"
        required
        placeholder="Choose a day"
        options={yearDayOptions(month).map((value) => ({
          value: String(value),
          text: String(value),
        }))}
        selectedOptions={day === null ? [] : [String(day)]}
        onOptionSelect={(values) => {
          if (values[0]) onChange({ ...draft, yearDate: { month, day: Number(values[0]) } });
        }}
        listboxMaxHeight="16rem"
        disabled={disabled}
        testId="schedule-year-day"
      />
    </div>
  );
}

/**
 * Once (round 10, C1): "Run 1: [date] [Run at]", one row per run, at most `MAX_RUN_DATES`. The
 * date is a calendar day in the schedule's zone, a native date field like Skip these dates; the
 * time is the same quarter-hour picker as the other frequencies. Shown only while
 * `ONCE_AVAILABLE` lets the segment be chosen.
 */
function RunDatesFieldset({
  draft,
  onChange,
  rowIds,
  disabled,
  revealIncompleteRows,
}: {
  draft: ScheduleDraft;
  onChange: (draft: ScheduleDraft) => void;
  rowIds: { ids: number[]; add: () => void; remove: (index: number) => void };
  disabled: boolean;
  revealIncompleteRows: boolean;
}): ReactElement {
  const rows = draft.runDates.length === 0 ? [{ date: null, time: null }] : draft.runDates;
  const issues = draft.runDates.length === 0 ? [null] : runDateRowIssues(draft);
  const today = todayInZone(draft.timezone);
  const setRow = (index: number, row: { date: string | null; time: string | null }): void =>
    onChange({ ...draft, runDates: rows.map((existing, i) => (i === index ? row : existing)) });
  const hasIncomplete = rows.some((row) => row.date === null || row.time === null);
  return (
    <fieldset className="border-0 p-0" data-testid="schedule-run-dates">
      <legend className="mb-2">
        <SkyText size={300} weight="semibold">
          Run dates
        </SkyText>
      </legend>
      <div className="flex flex-col gap-3">
        {rows.map((row, index) => {
          const issue = issues[index] ?? null;
          const shown = issue === INCOMPLETE_RUN_DATE_ISSUE && !revealIncompleteRows ? null : issue;
          return (
            <fieldset
              key={`run-${rowIds.ids[index]}`}
              aria-label={`Run ${index + 1}`}
              className="flex flex-wrap items-start gap-2 border-0 p-0"
              data-testid={`schedule-run-date-${index}`}
            >
              <SkyText size={300} weight="semibold" className="mt-7 w-14 shrink-0">
                {`Run ${index + 1}:`}
              </SkyText>
              <SkyInput
                type="date"
                label="Date"
                value={row.date ?? ""}
                min={today}
                onChange={(event) => setRow(index, { ...row, date: event.target.value || null })}
                disabled={disabled}
                validationState={shown ? "error" : "none"}
                validationMessage={shown ?? undefined}
                testId={`schedule-run-date-${index}-date`}
              />
              <SkyTimePicker
                label="Run at"
                placeholder="Choose a time"
                timeFormat="12h"
                minuteStep={QUARTER_HOUR_STEP}
                showDone
                showNow={false}
                showClear={false}
                value={toPickerTime(row.time)}
                onSelectTime={(time) => setRow(index, { ...row, time: fromPickerTime(time) })}
                disabled={disabled}
                testId={`schedule-run-date-${index}-picker`}
              />
              {rows.length > 1 ? (
                <SkyButton
                  appearance="subtle"
                  icon={DismissIcon}
                  className="mt-6"
                  aria-label={`Remove run ${index + 1}`}
                  disabled={disabled}
                  onClick={() => {
                    rowIds.remove(index);
                    onChange({ ...draft, runDates: rows.filter((_, i) => i !== index) });
                  }}
                />
              ) : null}
            </fieldset>
          );
        })}
      </div>
      <SkyButton
        appearance="subtle"
        size="small"
        icon={AddIcon}
        className="mt-2"
        disabled={disabled || hasIncomplete || rows.length >= MAX_RUN_DATES}
        onClick={() => {
          rowIds.add();
          onChange({ ...draft, runDates: [...rows, { date: null, time: null }] });
        }}
        testId="schedule-add-run-date"
      >
        Add another run time
      </SkyButton>
      <SkyText size={200} color="muted" block className="mt-1">
        {`Dates and times are in this schedule's time zone, on the quarter hour. At most ${MAX_RUN_DATES} runs.`}
      </SkyText>
    </fieldset>
  );
}

/**
 * The Frequency pills' layout (K2 item 1). The radio group's card variant wraps freely with 16 px
 * padding, so eight pills fell onto two lines with one alone on the second. The pills sit on a
 * grid instead: all eight on one row from `xl` (1280 px, sidebar open), two even rows of four
 * below it, never one pill alone. `[&>div]` is the group's flex root, `[&>div>div]` each pill;
 * the grid beats the group's own `flex` because the variant is `!`.
 */
export const FREQUENCY_GROUP_CLASS =
  "[&>div]:grid! [&>div]:grid-cols-4 [&>div]:gap-2 xl:[&>div]:grid-cols-8 [&>div>div]:gap-2 [&>div>div]:px-2.5 [&>div>div]:py-2";

/** The segmented Frequency control (round 10, C1): one radio group, Once disabled until it runs. */
function FrequencyControl({
  value,
  onChange,
  disabled,
}: {
  value: ScheduleSegment | null;
  onChange: (segment: ScheduleSegment) => void;
  disabled: boolean;
}): ReactElement {
  return (
    <SkyRadioGroup
      label="Frequency"
      required
      variant="card"
      size="small"
      layout="horizontal"
      className={FREQUENCY_GROUP_CLASS}
      // "" is a value no option has: the group stays controlled with nothing chosen.
      value={value ?? ""}
      onChange={(segment) => onChange(segment as ScheduleSegment)}
      options={SCHEDULE_SEGMENTS.map((segment) =>
        segment === "Once" && !ONCE_AVAILABLE
          ? { value: segment, label: segment, disabled: true, description: ONCE_COMING_SOON }
          : { value: segment, label: segment },
      )}
      disabled={disabled}
      testId="schedule-frequency"
    />
  );
}

/** The resolved bar's title: it runs, nothing is chosen yet, or the time needs a look. */
function resolvedTitle(ok: boolean, pending: boolean): string {
  if (ok) return "This schedule runs";
  return pending ? "Not scheduled yet" : "Check the time";
}

/** The resolved schedule in words: what it runs, that nothing is chosen yet, or what to check. */
function resolvedBar(
  resolved: ResolvedSchedule,
  pending: boolean,
  announcement: string,
): ReactElement {
  return (
    <SkyMessageBar
      intent={resolved.ok || pending ? "info" : "error"}
      layout="multiline"
      title={resolvedTitle(resolved.ok, pending)}
      message={
        resolved.ok ? (
          <>
            {announcement}{" "}
            {/* Round 10 wave 2 (S3): Once has dates, no expression and no skip dates. */}
            {resolved.cron !== null ? (
              <>
                <SkyText size={200} font="monospace" color="muted">
                  Cron equivalent {resolved.cron}
                </SkyText>
                <SkyText size={200} block className="mt-1" testId="schedule-skip-rule">
                  {SKIP_RULE_SENTENCE}
                </SkyText>
              </>
            ) : null}
          </>
        ) : (
          announcement
        )
      }
    />
  );
}

/** The card's notices: no upcoming run, paused until a date, and a kept custom expression. */
function ScheduleNotices({
  noUpcomingRun,
  draft,
  keptCustom,
  displayZone,
}: {
  noUpcomingRun: boolean;
  draft: ScheduleDraft;
  keptCustom: string | null;
  displayZone: string;
}) {
  return (
    <>
      {noUpcomingRun ? (
        <SkyMessageBar
          ref={quietBar}
          intent="warning"
          layout="multiline"
          title={NO_UPCOMING_RUN_EDITOR}
          message="Every firing in the next two years falls on a skipped date, or this schedule's time never occurs. It stays enabled. Remove a skip date or change the time, then save."
          testId="schedule-no-upcoming-run"
        />
      ) : null}

      {/* A disabled schedule does not start again by itself, and the list hides its badge then
        (`SchedulesTable`), so the bar is for an enabled schedule only (review r2 nit). */}
      {draft.enabled && draft.pausedUntil && isPaused(draft.pausedUntil) ? (
        <SkyMessageBar
          ref={quietBar}
          intent="info"
          layout="multiline"
          title={`Paused until ${formatPauseEnd(draft.pausedUntil, displayZone)}`}
          message="No import starts before then. It starts again by itself. Resume it from the Schedules list to end the pause sooner."
          testId="schedule-paused"
        />
      ) : null}

      {keptCustom ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          title={`Custom schedule: ${describeCron(keptCustom)}`}
          message="This schedule was set outside this editor. It is kept exactly as it is until you choose a frequency and a time below."
          testId="schedule-custom-cron"
        />
      ) : null}
    </>
  );
}

/**
 * The fields under the time zone for the chosen frequency: none for Hourly, the interval for a
 * Custom one, the dates for Once, else the times of day (`times`, built only when shown).
 */
function segmentFields({
  segment,
  isCustom,
  draft,
  onChange,
  disabled,
  runDateRowIds,
  revealIncompleteRows,
  times,
}: {
  segment: ScheduleSegment | null;
  isCustom: boolean;
  draft: ScheduleDraft;
  onChange: (next: ScheduleDraft) => void;
  disabled: boolean;
  runDateRowIds: ReturnType<typeof useTimeRowIds>;
  revealIncompleteRows: boolean;
  times: () => ReactElement;
}): ReactElement | null {
  if (segment === "Hourly") return null;
  if (isCustom) return <IntervalFieldset draft={draft} onChange={onChange} disabled={disabled} />;
  if (segment === "Once") {
    return (
      <RunDatesFieldset
        draft={draft}
        onChange={onChange}
        rowIds={runDateRowIds}
        disabled={disabled}
        revealIncompleteRows={revealIncompleteRows}
      />
    );
  }
  return times();
}

export function ScheduleTimeSection({
  draft,
  onChange,
  storedTimezone = null,
  fieldErrors = {},
  disabled = false,
  storedNoUpcomingRun = false,
  revealIncompleteRows = false,
  ref,
}: ScheduleTimeSectionProps) {
  const headingId = useId();
  const resolved = useMemo(() => resolveSchedule(draft), [draft]);
  /**
   * The control to focus once the rows re-render (a test id). "Add another time" disables itself
   * and a Remove button removes itself, so without this focus would fall to the page body. Every
   * handler that sets it also changes state; the effect below keeps it until the control exists.
   */
  const focusNext = useRef<string | null>(null);
  const timesRef = useRef<HTMLFieldSetElement>(null);
  useEffect(() => {
    const testId = focusNext.current;
    if (testId === null) return;
    const target = timesRef.current?.querySelector<HTMLElement>(
      `[data-testid="${testId}"] input, button[data-testid="${testId}"]`,
    );
    // Keep the hand-off until its control is on the page (#1132): a render that lands between
    // the click and the render that adds the row (a pending effect, a parent's later state)
    // would otherwise clear it and focus would stay on the button that disabled itself.
    if (!target) return;
    focusNext.current = null;
    if (!(target as HTMLButtonElement).disabled) target.focus();
  });

  const storedOutsideList =
    storedTimezone && !isSelectableTimezone(storedTimezone) ? storedTimezone : null;

  // Round 10, C1 (rewriting walk 2026-10-02, D6): each option reads the zone and the time there
  // with "now", "Pacific (PDT) — now 9:25 AM", so it is not read as a run time; the closed field
  // reads "Pacific (PDT)" alone. The minute clock keeps both current, and turns PDT into PST on
  // the night it changes.
  const now = useMinuteClock();
  const timezones = useMemo(
    () =>
      offeredTimezones(storedOutsideList).map((zone) => ({
        value: zone,
        text: timezonePickerLabel(zone, now),
        selectedText: timezoneShortLabel(zone, now),
      })),
    [storedOutsideList, now],
  );
  const displayZone = useDisplayTz();
  const segment = shownSegment(draft);
  const summaryLine = scheduleSummaryLine(draft, draft.timezone, now, displayZone);

  const setWeekDays = (states: CheckboxState[] | boolean): void => {
    if (typeof states === "boolean") return;
    onChange({
      ...draft,
      weekDays: states.filter((s) => s.checked).map((s) => Number.parseInt(s.id, 10)),
    });
  };

  /** A draft with no time shows one empty row that is not in the draft until it gets a time. */
  const rows: Array<string | null> = draft.times.length === 0 ? [null] : draft.times;
  const rowIssues = draft.times.length === 0 ? [null] : timeRowIssues(draft);
  const rowIds = useTimeRowIds(rows.length);
  const runDateRowIds = useTimeRowIds(Math.max(1, draft.runDates.length));

  const setTime = (index: number, value: string | null): void => {
    if (draft.times.length === 0) {
      if (value !== null) onChange({ ...draft, times: [value] });
      return;
    }
    onChange({
      ...draft,
      times: draft.times.map((existing, i) => (i === index ? value : existing)),
    });
  };

  const removeTime = (index: number): void => {
    const removed = draft.times[index];
    const times = draft.times.filter((_, i) => i !== index);
    // A row with no time came from "Add another time", so focus goes back there; otherwise to
    // the row that took this one's place, else the one before it.
    focusNext.current =
      removed === null
        ? "schedule-add-time"
        : `schedule-time-${Math.max(0, Math.min(index, times.length - 1))}-picker`;
    rowIds.remove(index);
    onChange({ ...draft, times });
  };

  const hasEmptyRow = rows.some((time) => time === null);
  const pending = !hasChosenTime(draft) && !draft.customCron;
  const keptCustom = keptCustomCron(draft);
  const isCustom = segment === "Custom";
  // Round 5: a stored time off the quarter hour (02:07) is kept, and said so under the rows.
  const offQuarterMinute = offQuarterMinuteOf(draft);
  // What the server said about the expression itself (`cron`, or any `friendly.*` path), shown
  // on this card rather than only in the message above Save.
  const scheduleIssue = scheduleIssueOf(fieldErrors);

  const announcement = announceSchedule(resolved, draft.timezone, displayZone, draft.enabled);

  return (
    <section
      ref={ref}
      tabIndex={-1}
      aria-labelledby={headingId}
      data-testid="schedule-time-section"
      className="rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <SkyCard
        appearance="outline"
        header={{
          title: (
            <SkyText as="h2" id={headingId} size={400} weight="semibold">
              When
            </SkyText>
          ),
          description: "How often, at what time, and in which time zone",
        }}
      >
        <ScheduleNotices
          noUpcomingRun={storedNoUpcomingRun}
          draft={draft}
          keptCustom={keptCustom}
          displayZone={displayZone}
        />

        <div>
          <FrequencyControl
            value={segment}
            onChange={(next) => onChange(applySegment(draft, next))}
            disabled={disabled}
          />
          {summaryLine ? (
            <SkyText size={300} block className="mt-2" testId="schedule-summary-line">
              {summaryLine}
            </SkyText>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
          <div>
            <SkyDropdown
              label="Time zone"
              required
              options={timezones}
              selectedOptions={draft.timezone ? [draft.timezone] : []}
              onOptionSelect={(values) =>
                onChange({ ...draft, timezone: values[0] ?? draft.timezone })
              }
              // Round 7 T-36: tall enough for every zone with no scroll (the eight, plus a
              // stored zone outside them): 18rem left UTC, the eighth, under the fold.
              listboxMaxHeight="min(420px, 70vh)"
              disabled={disabled}
              validationState={fieldErrors.timezone ? "error" : "none"}
              validationMessage={fieldErrors.timezone}
              testId="schedule-timezone"
            />
            {/* Round 6, W8: one time rule per screen. Every time this card asks for is in the
                schedule's zone; the list and Overview show runs in the viewer's own zone. */}
            <ZoneNotes timezone={draft.timezone} storedOutsideList={storedOutsideList} />
          </div>
        </div>

        {/* Called, not rendered as a component: the fieldset is the card's own child. */}
        {segmentFields({
          segment,
          isCustom,
          draft,
          onChange,
          disabled,
          runDateRowIds,
          revealIncompleteRows,
          times: () => (
            <TimesFieldset
              timesRef={timesRef}
              rows={rows}
              rowIds={rowIds.ids}
              rowIssues={rowIssues}
              revealIncompleteRows={revealIncompleteRows}
              disabled={disabled}
              canAdd={!hasEmptyRow && draft.times.length < 24}
              offQuarterMinute={offQuarterMinute}
              onTimeChange={setTime}
              onRemove={removeTime}
              onAdd={() => {
                // This button disables itself; the new row's picker is where the user goes next.
                focusNext.current = `schedule-time-${draft.times.length}-picker`;
                rowIds.add();
                onChange({ ...draft, times: [...draft.times, null] });
              }}
            />
          ),
        })}

        {segment === "Yearly" ? (
          <YearDateFields draft={draft} onChange={onChange} disabled={disabled} />
        ) : null}

        {segment === "Weekly" || isCustom ? (
          <div>
            <SkyCheckbox
              label="Days of the week"
              layout="horizontal"
              options={WEEK_DAYS.map((day) => ({ id: String(day.value), label: day.short }))}
              // Controlled, unlike the donor's `defaultChecked`: the draft is
              // also written by loading a saved schedule, and an uncontrolled
              // group would keep showing the days the form opened with.
              value={draft.weekDays.map(String)}
              onChange={setWeekDays}
              disabled={disabled}
              testId="schedule-week-days"
            />
            <SkyText size={200} color="muted" block className="mt-1">
              No day chosen means every day.
            </SkyText>
          </div>
        ) : null}

        {segment === "Monthly" ? (
          <div>
            <SkyDropdown
              label="Days of the month"
              multiselect
              options={MONTH_DAYS.map((day) => ({ value: String(day), text: String(day) }))}
              selectedOptions={draft.monthDays.map(String)}
              onOptionSelect={(values) =>
                onChange({ ...draft, monthDays: values.map(Number).sort((a, b) => a - b) })
              }
              listboxMaxHeight="16rem"
              disabled={disabled}
              testId="schedule-month-days"
            />
            <SkyText size={200} color="muted" block className="mt-1">
              A month shorter than the day you choose skips that month.
            </SkyText>
          </div>
        ) : null}

        {/* Round 10 wave 2 (S3): Once runs on its own dates; skip dates do not apply to it. */}
        {segment === "Once" ? null : (
          <ScheduleSkipDates
            draft={draft}
            onChange={onChange}
            disabled={disabled}
            error={fieldErrors.skipDates}
          />
        )}

        {scheduleIssue ? (
          <SkyMessageBar
            intent="error"
            layout="multiline"
            title="The server did not accept this time"
            message={scheduleIssue}
            testId="schedule-time-error"
          />
        ) : null}

        {/* The resolved schedule, in words. `aria-live="polite"` lives on the
            wrapper rather than on the message bar so the region exists from the
            first render: a live region added to the DOM at the same moment as
            its text is not announced by most screen readers. A schedule with no
            time chosen yet is not an error, so it reads as information. The skip
            rule does not change, so a polite region (not atomic) does not repeat it. */}
        <div aria-live="polite" data-testid="schedule-resolved">
          {resolvedBar(resolved, pending, announcement)}
        </div>
      </SkyCard>
    </section>
  );
}
